import { create } from "zustand";
import { startChartPerfSession } from "../utils/chartPerf";

/**
 * Custom hook using Zustand for chart state management, including modal toggle handling.
 *
 * This store centralizes the state and state-updaters for chart-related data, such as
 * chart data, selected indicators, asset keys, and UI state. Additionally, it provides
 * a consistent interface to handle modal open/close toggles that relate to chart interactions,
 * making it easy to coordinate modal dialogs for advanced chart controls.
 *
 * @returns {Object} Zustand store with chart state and state-setters.
 * @description
 * - Manages the state for chart data, indicators, status, URL, asset key, etc.
 * - Provides setter actions for all state pieces.
 * - Intended for handling UI modals (open/close) and toggle logic related to chart views.
 *
 * @example
 * const {
 *   chartData,
 *   setChartData,
 *   chartStatus,
 *   setChartStatus,
 *   // ... other state & setters
 * } = useChartStore();
 *
 * // Set chart data:
 * setChartData(newChartData);
 *
 * // Open or close a modal:
 * setChartStatus('openModal');
 */
const useChartStore = create((set) => ({
  chartData: {},
  lastMatchedDate: null,
  chartIndicator: {},
  chartExpertIndicator: null,
  qouteCoin: null,
  chartIndicators: null,
  charSelectedtStrategy: null,
  chartDataUrl: null,
  chartStatus: null,
  chartReportId: null,
  chartAssetKey: null,
  chartPerfSessionId: null,
  setChartOpenPayload: ({
    status,
    charts,
    indicator,
    reportId,
    assetKey,
    lastMatchedDate,
    strategy,
  }) => {
    const indicatorKey =
      typeof indicator === "string" ? indicator : String(indicator?.key || "").trim() || null;

    const perfSessionId = startChartPerfSession({
      status: status || null,
      reportId: reportId || null,
      assetKey: assetKey || null,
      indicatorKey,
      hasStrategy: Boolean(strategy),
    });

    return set(() => ({
      chartStatus: status,
      chartData: charts,
      chartIndicator: indicator,
      chartReportId: reportId,
      qouteCoin: assetKey,
      lastMatchedDate,
      chartPerfSessionId: perfSessionId,
      ...(strategy
        ? {
            chartAssetKey: assetKey,
            charSelectedtStrategy: strategy,
          }
        : {}),
    }));
  },
  setChartPerfSessionId: (value) => set({ chartPerfSessionId: value }),
  setChartAssetKey: (value) => set({ chartAssetKey: value }),
  setChartReportId: (value) => set({ chartReportId: value }),
  setChartDataUrl: (data) => set({ chartDataUrl: data }),
  setChartData: (data) => set({ chartData: data }),
  setChartIndicator: (value) => set({ chartIndicator: value }),
  setQouteCoin: (value) => set({ qouteCoin: value }),
  setChartIndicators: (values) => set({ chartIndicators: values }),
  setCharSelectedtStrategy: (value) => set({ charSelectedtStrategy: value }),
  setChartExpertIndicator: (value) => set({ chartExpertIndicator: value }),
  setLastMatchedDate: (value) => set({ lastMatchedDate: value }),
  setChartStatus: (value) => set({ chartStatus: value }),
}));

export default useChartStore;
